/**
 * Tarja fixa no topo identificando um ambiente que NÃO é produção.
 *
 * Existe por um erro específico e caro: o mesmo operador abre o sandbox numa aba
 * para treinar e a produção em outra, as duas telas são idênticas, e ele cadastra
 * um paciente de verdade no ambiente de treino — ou pior, faz o treinamento em
 * cima da produção. Nenhum aviso na tela de login resolve isso: a confusão
 * acontece meia hora depois, no meio do trabalho. A tarja tem de estar visível
 * em TODAS as telas, o tempo todo.
 *
 * Só aparece quando `VITE_ENVIRONMENT_LABEL` está definida no build. Em
 * produção a variável não existe e o componente não renderiza nada — nenhum
 * risco de a tarja vazar para o ambiente real.
 *
 * O deslocamento do conteúdo é feito por CSS (`body.env-banner` em index.css) e
 * não aqui: a aplicação tem dezenas de cabeçalhos `sticky top-0` que ficariam
 * escondidos atrás da tarja, e corrigir um a um seria impossível de manter.
 */
import { useEffect } from 'react'

/**
 * O roxo `primary` da marca. As telas são construídas quase todas em
 * `secondary` (o azul), então o roxo aparece pouco e a tarja continua se
 * destacando — sem introduzir uma cor de fora da paleta.
 *
 * Hex literal em vez de `var(--color-primary)` porque o mesmo valor é
 * interpolado dentro do SVG do favicon, onde custom property não resolve.
 */
const ACCENT = '#543c9a'

const LABEL = import.meta.env.VITE_ENVIRONMENT_LABEL as string | undefined
const DESCRIPTION = import.meta.env.VITE_ENVIRONMENT_DESCRIPTION as
    | string
    | undefined

/**
 * Ícone da aba: quadrado magenta com a inicial do ambiente.
 *
 * Com muitas abas abertas o navegador encolhe cada uma até sobrar só o ícone —
 * o `[SANDBOX]` do título já não aparece, e as duas abas ficam idênticas. É
 * exatamente o momento em que alguém clica na errada.
 */
const buildFavicon = (letter: string): string => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <rect width="64" height="64" rx="12" fill="${ACCENT}"/>
        <text x="32" y="46" font-family="system-ui,sans-serif" font-size="42"
              font-weight="700" fill="#ffffff" text-anchor="middle">${letter}</text>
    </svg>`
    return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

export default function EnvironmentBanner() {
    useEffect(() => {
        if (!LABEL) return
        document.body.classList.add('env-banner')
        // O título da aba é o que diferencia as duas abas na barra do navegador —
        // é lá que o operador escolhe errado antes de olhar a tela.
        const original = document.title
        document.title = `[${LABEL}] ${original}`

        // Troca todos os `rel` de ícone: o Chrome pode escolher qualquer um
        // deles, e um esquecido faz o ícone de produção voltar sozinho.
        const icons = [
            ...document.querySelectorAll<HTMLLinkElement>(
                'link[rel~="icon"], link[rel="apple-touch-icon"]'
            ),
        ]
        const previous = icons.map(link => link.href)
        const favicon = buildFavicon(LABEL.charAt(0).toUpperCase())
        icons.forEach(link => {
            link.href = favicon
        })

        return () => {
            document.body.classList.remove('env-banner')
            document.title = original
            icons.forEach((link, i) => {
                link.href = previous[i]!
            })
        }
    }, [])

    if (!LABEL) return null

    return (
        <>
            <div
                role="status"
                aria-label={`Ambiente ${LABEL}`}
                className="env-banner-bar fixed inset-x-0 top-0 z-[9999] flex h-6 items-center justify-center gap-2 px-3 text-center text-[11px] font-bold uppercase tracking-wide text-white"
                style={{ backgroundColor: ACCENT }}
            >
                <span>{LABEL}</span>
                {DESCRIPTION && (
                    <span className="hidden truncate font-semibold normal-case opacity-90 sm:inline">
                        {DESCRIPTION}
                    </span>
                )}
            </div>
            {/* Moldura na borda da janela: presença sem custar altura, já que a
                barra precisa continuar fina. `pointer-events-none` para não
                interceptar clique nenhum, e `inset` para não gerar rolagem. */}
            <div
                aria-hidden
                className="pointer-events-none fixed inset-0 z-[9998]"
                style={{ boxShadow: `inset 0 0 0 2px ${ACCENT}` }}
            />
        </>
    )
}
